// Ported from relay-protocol packages/relay-widget/src/recoveryGroups.ts
// (upstream e5bff75). Pure grouping for the "Recover stuck funds" panel --
// no React, no wallet, no fetch.
//
// The unit the panel must be organized around is the FORWARDER ADDRESS, not the
// delivery-token symbol, and getting that wrong is not cosmetic.
//
// A forwarder is derived from (recipient, version) plus the factory and
// constructor args of the target that issued it (see the backend's
// forwarderForTargetAt). For a "depositor"-kind target (e.g. pONEUSD) the
// target's own factory and depositor go into the derivation, so its forwarder is
// genuinely distinct. For a "swap"-kind target (e.g. ONEUSD) the target does NOT
// enter the derivation at all -- every swap-kind target resolves to the SAME
// forwarder for the same recipient.
//
// So a panel laid out per target would, the moment a deployment offers two
// swap-kind targets, show one balance twice with a Recover button under each.
// Both buttons would move the whole balance and the second would revert on
// `NothingToForward` -- which is precisely the hazard that makes per-ORIGIN
// splitting unacceptable (one balance, no amount parameter on sweepToUser), now
// reintroduced on a different axis. Grouping by forwarder is what keeps "one
// balance, one button" true no matter how the catalog's targets are configured.
//
// It also fixes the aggregate count: summing a per-target count double-counted
// every forwarder shared between targets. See countRecoverableTokens.
import type { OrderSummary, RecoveryInfo } from './types'

/** The backend state in which a forwarder still HOLDS the funds an order is
 * about. Funds leave at BSC_FUNDED -> BRIDGE_INITIATED, so this is the only
 * status whose deposits can explain a live balance -- see the backend's
 * domain.allowed state map. */
const FUNDS_AT_FORWARDER_STATUS = 'BSC_FUNDED'

/** One delivery target and the recovery info fetched for it. */
export interface RecoveryTargetEntry {
  /** Delivery-token symbol from `Catalog.targets`, e.g. "pONEUSD". */
  symbol: string
  /** True for the catalog's default target. Orders report `target: ""` for it,
   * so matching an order to a target needs to know which symbol "" means. */
  isDefault: boolean
  info: RecoveryInfo
}

/** One deposit that makes up a forwarder's current balance. A read-only
 * provenance row -- see ForwarderGroup.deposits for why it can never become a
 * per-origin recover button. */
export interface GroupedDeposit {
  /** Amount that arrived at the forwarder, in BSC USDT base units. */
  amount: string
  /** The chain the user actually paid from. Absent for an unattributed deposit
   * (a direct BSC deposit has no cross-chain leg and never gets one). */
  originChainId?: number
  /** The user's own send on the origin chain. Absent when unattributed. */
  originTxHash?: string
  /** The BSC-side arrival. Always present -- it is how the deposit was seen. */
  txHash: string
  blockNumber: number
}

/** One forwarder, the delivery targets that share it, and everything the panel
 * needs to render exactly one recovery section for it. */
export interface ForwarderGroup {
  /** The forwarder address, in the casing the API reported it. */
  forwarder: string
  /** Every delivery-token symbol resolving to this forwarder, in catalog order.
   * More than one means they share it by derivation (all swap-kind targets do). */
  targets: string[]
  /** The recovery info for this forwarder. When several targets share it, they
   * report identical forwarder/balance/tokens, and their factory is identical by
   * config invariant (a swap-kind target's factory must be empty or the standing
   * factory), so the first entry's info stands for the group. A mismatched
   * factory would in any case fail closed at signing: verifyForwarder checks it
   * against the pinned list AND against the chain's own derivation. */
  info: RecoveryInfo
  /**
   * The individual deposits behind this forwarder's current balance, oldest
   * first -- read-only provenance, so a user who topped up from two chains can
   * see that the single balance is made of their two sends.
   *
   * It is deliberately NOT a set of per-origin actions. There is one balance in
   * one contract and `sweepToUser` takes no amount, so splitting the button per
   * origin would have both buttons move the entire balance with the second
   * reverting. Showing the composition answers the question ("where did my 2.0
   * come from?") without inventing an action that cannot exist.
   *
   * Empty when no order for this forwarder is currently BSC_FUNDED -- e.g. a
   * deposit seen on-chain but not yet swept into an order, or residual dust from
   * an order already written off. That is exactly why the sum of these rows can
   * be less than the balance, and why callers must say so rather than present
   * this as a complete decomposition (see depositsTotal).
   */
  deposits: GroupedDeposit[]
  /** Sum of `deposits[].amount`, in the same base units as `info.balance`.
   * Compare the two: when this is smaller, part of the balance has no listed
   * provenance and the UI must not imply otherwise. */
  depositsTotal: bigint
}

/** Parses a base-unit amount string, treating anything malformed as zero.
 * Amounts here are display/provenance only -- never load-bearing -- so one bad
 * row must not throw during render (the same rule every other formatter in this
 * package follows). */
function toAmount(raw: string | undefined): bigint {
  if (!raw) return 0n
  try {
    const v = BigInt(raw)
    return v > 0n ? v : 0n
  } catch {
    return 0n
  }
}

/** The target symbol an order refers to: its own, or the catalog default's when
 * the order reports "" (the backend's encoding for "the default target"). */
function orderTarget(order: OrderSummary, defaultSymbol: string | undefined): string | undefined {
  return order.target || defaultSymbol
}

/**
 * Groups per-target recovery info by forwarder address, attaching the deposits
 * that explain each forwarder's balance.
 *
 * Entries are expected in catalog order; the returned groups follow the order in
 * which their forwarder was first seen, so the layout is stable across refetches
 * rather than reordering under the user.
 *
 * Addresses are keyed case-insensitively: the API's checksum casing is not
 * guaranteed to be stable, and two spellings of one address must never become
 * two sections with two buttons on the same balance.
 */
export function groupForwarders(
  entries: readonly RecoveryTargetEntry[],
  orders: readonly OrderSummary[] = [],
): ForwarderGroup[] {
  const defaultSymbol = entries.find((e) => e.isDefault)?.symbol

  const byForwarder = new Map<string, ForwarderGroup>()
  for (const entry of entries) {
    if (!entry.info?.forwarder) continue
    const key = entry.info.forwarder.toLowerCase()
    const existing = byForwarder.get(key)
    if (existing) {
      existing.targets.push(entry.symbol)
      continue
    }
    byForwarder.set(key, {
      forwarder: entry.info.forwarder,
      targets: [entry.symbol],
      info: entry.info,
      deposits: [],
      depositsTotal: 0n,
    })
  }

  // Second pass, once every group knows its full target set: an order maps to a
  // forwarder through its target, so this cannot be folded into the loop above
  // without missing orders belonging to a target seen later.
  for (const group of byForwarder.values()) {
    const symbols = new Set(group.targets)
    for (const order of orders) {
      if (order.status !== FUNDS_AT_FORWARDER_STATUS) continue
      const target = orderTarget(order, defaultSymbol)
      if (!target || !symbols.has(target)) continue
      for (const d of order.deposits ?? []) {
        group.deposits.push({
          amount: d.amount,
          originChainId: d.originChainId,
          originTxHash: d.originTxHash,
          txHash: d.txHash,
          blockNumber: d.blockNumber,
        })
        group.depositsTotal += toAmount(d.amount)
      }
    }
    group.deposits.sort((a, b) => a.blockNumber - b.blockNumber)
  }

  return [...byForwarder.values()]
}

/** Groups holding something worth acting on -- a recoverable token, or (for a
 * backend predating `tokens[]`) a nonzero primary balance the server hasn't
 * called "none". These are the sections the panel renders. */
export function groupsWithFunds(groups: readonly ForwarderGroup[]): ForwarderGroup[] {
  return groups.filter((g) => {
    if ((g.info.tokens ?? []).some((t) => t.recoverable)) return true
    return toAmount(g.info.balance) > 0n && g.info.suggestedAction !== 'none'
  })
}

/**
 * Total recoverable tokens across every forwarder -- the gear badge's number.
 *
 * Counting per forwarder rather than per target is the point: two targets
 * sharing a forwarder describe ONE pot of money, and summing their per-target
 * counts reported it twice. The badge then read higher than the panel could ever
 * list, which looks like the panel is hiding something.
 */
export function countRecoverableTokens(groups: readonly ForwarderGroup[]): number {
  return groups.reduce((sum, g) => sum + (g.info.tokens ?? []).filter((t) => t.recoverable).length, 0)
}
